// contexts/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

// Mock Admin User
const mockAdmin = {
  id: 'admin_001',
  name: 'Admin User',
  email: 'admin@haitianservicesplatform.org',
  phone: '+509 0000-0000',
  role: 'admin',
  profileImage: { url: 'https://i.pravatar.cc/150?img=11' },
  isAdmin: true,
  isEmailVerified: true,
  isProfileComplete: true,
  requiresPINVerification: false,
  permissions: [
    'manage_providers',
    'manage_donations',
    'manage_reports',
    'manage_settings',
    'view_analytics',
    'manage_users'
  ],
  createdAt: new Date().toISOString()
};

// Mock Provider User
const mockProvider = {
  id: 'provider_001',
  name: 'Jean Pierre',
  email: 'provider@haitianservicesplatform.org',
  phone: '+509 3456-7890',
  role: 'service_provider',
  profileImage: { url: 'https://i.pravatar.cc/150?img=1' },
  businessName: 'JP Electrical Services',
  serviceType: 'Electrician',
  isAdmin: false,
  isProfileComplete: true,
  isEmailVerified: true,
  requiresPINVerification: false,
  createdAt: new Date().toISOString()
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Error loading user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password, role) => {
    try {
      // Check for admin login
      if (email === 'admin@haitianservicesplatform.org' && password === 'admin123') {
        localStorage.setItem('user', JSON.stringify(mockAdmin));
        setUser(mockAdmin);
        setIsAuthenticated(true);
        return { success: true, user: mockAdmin };
      }

      // Check for provider login
      if (role === 'service_provider' && email === 'provider@haitianservicesplatform.org' && password === 'provider123') {
        localStorage.setItem('user', JSON.stringify(mockProvider));
        setUser(mockProvider);
        setIsAuthenticated(true);
        return { success: true, user: mockProvider };
      }

      // For demo - accept any email with matching role
      if (role === 'service_provider') {
        const mockUser = {
          id: '2',
          name: email.split('@')[0].replace(/[^a-zA-Z]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          email: email,
          phone: '+509 3456-7890',
          role: 'service_provider',
          profileImage: { url: 'https://i.pravatar.cc/150?img=2' },
          businessName: `${email.split('@')[0]}'s Services`,
          serviceType: 'General Services',
          isAdmin: false,
          isProfileComplete: true,
          isEmailVerified: true,
          requiresPINVerification: false,
          createdAt: new Date().toISOString()
        };
        
        localStorage.setItem('user', JSON.stringify(mockUser));
        setUser(mockUser);
        setIsAuthenticated(true);
        return { success: true, user: mockUser };
      }

      return { 
        success: false, 
        error: 'Invalid credentials. Use admin@haitianservicesplatform.org / admin123 or provider@haitianservicesplatform.org / provider123' 
      };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const loginAsAdmin = async () => {
    try {
      localStorage.setItem('user', JSON.stringify(mockAdmin));
      setUser(mockAdmin);
      setIsAuthenticated(true);
      return { success: true, user: mockAdmin };
    } catch (error) {
      console.error('Admin login error:', error);
      return { success: false, error: error.message };
    }
  };

  const loginAsProvider = async () => {
    try {
      localStorage.setItem('user', JSON.stringify(mockProvider));
      setUser(mockProvider);
      setIsAuthenticated(true);
      return { success: true, user: mockProvider };
    } catch (error) {
      console.error('Provider login error:', error);
      return { success: false, error: error.message };
    }
  };

  const register = async (userData) => {
    try {
      const mockUser = {
        id: '2',
        name: userData.name,
        email: userData.email,
        phone: userData.phone,
        role: 'service_provider',
        profileImage: { url: 'https://i.pravatar.cc/150?img=2' },
        businessName: userData.businessName,
        serviceType: userData.serviceType,
        hourlyRate: userData.hourlyRate,
        yearsOfExperience: userData.yearsOfExperience,
        description: userData.description,
        isAdmin: false,
        isProfileComplete: true,
        isEmailVerified: true,
        requiresPINVerification: false,
        createdAt: new Date().toISOString()
      };
      
      localStorage.setItem('user', JSON.stringify(mockUser));
      setUser(mockUser);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const updateUser = async (updatedData) => {
    try {
      const updatedUser = { ...user, ...updatedData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      return { success: true };
    } catch (error) {
      console.error('Update user error:', error);
      return { success: false, error: error.message };
    }
  };

  const getToken = () => {
    return localStorage.getItem('auth_token') || null;
  };

  const setToken = (token) => {
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  };

  const isAdmin = () => {
    return user?.isAdmin || false;
  };

  const isProvider = () => {
    return user?.role === 'service_provider' || false;
  };

  const isClient = () => {
    return user?.role === 'client' || false;
  };

  const getInitialRoute = () => {
    if (!isAuthenticated) return '/login';
    if (isAdmin()) return '/admin/overview';
    if (isProvider()) return '/provider-dashboard';
    return '/home';
  };

  // Mock login for development
  const quickLogin = (type) => {
    if (type === 'admin') {
      loginAsAdmin();
    } else if (type === 'provider') {
      loginAsProvider();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        login,
        loginAsAdmin,
        loginAsProvider,
        register,
        logout,
        updateUser,
        getToken,
        setToken,
        isAdmin,
        isProvider,
        isClient,
        getInitialRoute,
        quickLogin,
        mockAdmin,
        mockProvider
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// HOC to protect routes with authentication
export const withAuth = (WrappedComponent) => {
  return function WithAuthWrapper(props) {
    const { isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
      if (!isLoading && !isAuthenticated) {
        navigate('/login');
      }
    }, [isLoading, isAuthenticated, navigate]);

    if (isLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center min-vh-100">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    if (!isAuthenticated) {
      return null;
    }

    return <WrappedComponent {...props} />;
  };
};

// HOC to protect provider routes
export const withProviderAuth = (WrappedComponent) => {
  return function WithProviderAuthWrapper(props) {
    const { isAuthenticated, isLoading, isProvider, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
      if (!isLoading) {
        if (!isAuthenticated) {
          navigate('/login');
        } else if (!isProvider() && !user?.isAdmin) {
          navigate('/home');
        }
      }
    }, [isLoading, isAuthenticated, isProvider, user, navigate]);

    if (isLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center min-vh-100">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    if (!isAuthenticated || (!isProvider() && !user?.isAdmin)) {
      return null;
    }

    return <WrappedComponent {...props} />;
  };
};

// HOC to protect admin routes
export const withAdminAuth = (WrappedComponent) => {
  return function WithAdminAuthWrapper(props) {
    const { isAuthenticated, isLoading, isAdmin, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
      if (!isLoading) {
        if (!isAuthenticated) {
          navigate('/login');
        } else if (!isAdmin() && !user?.isAdmin) {
          navigate('/home');
        }
      }
    }, [isLoading, isAuthenticated, isAdmin, user, navigate]);

    if (isLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center min-vh-100">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    if (!isAuthenticated || !isAdmin()) {
      return null;
    }

    return <WrappedComponent {...props} />;
  };
};